import React, { useState, useEffect, useCallback } from 'react';
import type { PlaybookStatusFull, PlaybookStatusCategory } from '../../types/playbook.types';

interface StatusPropertyPanelProps {
  status: PlaybookStatusFull;
  onUpdate: (statusId: string, data: Partial<PlaybookStatusFull>) => Promise<void>;
  onDelete: (statusId: string) => Promise<void>;
}

const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';
const labelClass = 'block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5';

const StatusPropertyPanel: React.FC<StatusPropertyPanelProps> = ({ status, onUpdate, onDelete }) => {
  const [name, setName] = useState(status.name);
  const [slug, setSlug] = useState(status.slug);
  const [category, setCategory] = useState<PlaybookStatusCategory>(status.category);
  const [description, setDescription] = useState(status.description ?? '');
  const [isInitial, setIsInitial] = useState(status.is_initial ?? false);
  const [isTerminal, setIsTerminal] = useState(status.is_terminal ?? false);
  const [slaHours, setSlaHours] = useState(status.sla_hours?.toString() ?? '');
  const [escalationRole, setEscalationRole] = useState(status.escalation_role ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setName(status.name);
    setSlug(status.slug);
    setCategory(status.category);
    setDescription(status.description ?? '');
    setIsInitial(status.is_initial ?? false);
    setIsTerminal(status.is_terminal ?? false);
    setSlaHours(status.sla_hours?.toString() ?? '');
    setEscalationRole(status.escalation_role ?? '');
    setConfirmDelete(false);
  }, [status.id]);

  const save = useCallback(() => {
    onUpdate(status.id, {
      name,
      slug,
      category,
      description: description || undefined,
      is_initial: isInitial,
      is_terminal: isTerminal,
      sla_hours: slaHours ? Number(slaHours) : null,
      escalation_role: escalationRole || null,
    });
  }, [status.id, name, slug, category, description, isInitial, isTerminal, slaHours, escalationRole, onUpdate]);

  return (
    <div className="space-y-3 p-3" style={{ animation: 'fadeSlideIn 200ms ease-out' }}>
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Status Properties</h4>
        <span className="text-[9px] text-gray-400 font-mono">{status.id.slice(0, 8)}</span>
      </div>

      <div>
        <label className={labelClass}>Name</label>
        <input type="text" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} onBlur={save} />
      </div>

      <div>
        <label className={labelClass}>Slug</label>
        <input type="text" className={`${inputClass} font-mono`} value={slug} onChange={(e) => setSlug(e.target.value)} onBlur={save} />
      </div>

      <div>
        <label className={labelClass}>Category</label>
        <select className={inputClass} value={category} onChange={(e) => { setCategory(e.target.value as PlaybookStatusCategory); setTimeout(save, 0); }}>
          <option value="todo">Todo</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
        </select>
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <textarea className={inputClass} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} onBlur={save} />
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={isInitial} onChange={(e) => { setIsInitial(e.target.checked); setTimeout(save, 0); }} className="rounded border-gray-300 dark:border-gray-600" />
          Initial
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={isTerminal} onChange={(e) => { setIsTerminal(e.target.checked); setTimeout(save, 0); }} className="rounded border-gray-300 dark:border-gray-600" />
          Terminal
        </label>
      </div>

      <div>
        <label className={labelClass}>SLA Hours</label>
        <input type="number" className={inputClass} value={slaHours} onChange={(e) => setSlaHours(e.target.value)} onBlur={save} placeholder="Optional" />
      </div>

      <div>
        <label className={labelClass}>Escalation Role</label>
        <input type="text" className={inputClass} value={escalationRole} onChange={(e) => setEscalationRole(e.target.value)} onBlur={save} placeholder="e.g. supervising_attorney" />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
        {!confirmDelete ? (
          <button type="button" onClick={() => setConfirmDelete(true)} className="w-full rounded-md border border-rose-300 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 dark:border-rose-600 dark:text-rose-400 dark:hover:bg-rose-900/20">
            Delete Status
          </button>
        ) : (
          <div className="space-y-2">
            <p className="text-[10px] text-rose-600 dark:text-rose-400">This will also remove all connected transitions.</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => onDelete(status.id)} className="flex-1 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700">
                Confirm Delete
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)} className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusPropertyPanel;
